import { Client } from '@sr-sdks/api-sdk-axios';
import { Skeleton } from '@sr/ui-library';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { queryOptionsGetClient } from '../../../../api/workspaces/clientApi';
import {
  formatOptionalDate,
  formatRepresentationStatus,
  formatRepresentationType,
} from '../../../../components/ClientCard/clientMeta';
import ErrorPage from '../../../../components/ErrorPage';
import { HeaderField } from '../../../../components/HeaderField';
import { PageHeader } from '../../../../components/PageHeader';
import { ErrorFallback } from '../../../../components/QueryResults/ErrorFallback';
import { SingleQueryResults } from '../../../../components/QueryResults/SingleQueryResults';
import { useActiveWorkspace } from '../../../../hooks/useActiveWorkspace';
import { MSR3BaseLayout } from '../../../../layout/MSR3BaseLayout';
import { ClientStatementsSection } from './ClientStatementsSection';
import { ClientTasksSection } from './ClientTasksSection';
import { ClientTitlesSection } from './ClientTitlesSection';

/**
 * Sticky page header: client name + Representation / Representation Start
 * Date, mirroring `TitleSummaryHeader`'s Year of production / Type row
 * shape exactly, via the shared `HeaderField`. Representation Type is
 * appended only when it is Agency or Assignment — it affects competing
 * claims, but is meaningless for clients whose agent doesn't record it.
 */
const ClientSummaryHeader = ({ client }: { client: Client }) => {
  const representationStatus = formatRepresentationStatus(
    client.representationStatus,
  );
  const representationStartDate = formatOptionalDate(
    client.representationStartDate,
  );
  const representationType = formatRepresentationType(
    client.representationType,
  );

  return (
    <PageHeader title={client.clientName}>
      <div className="flex flex-wrap gap-6">
        <HeaderField
          label="Representation"
          value={representationStatus ?? ''}
        />
        <HeaderField
          label="Representation Start Date"
          value={representationStartDate ?? ''}
        />
        {representationType && (
          <HeaderField label="Representation Type" value={representationType} />
        )}
      </div>
    </PageHeader>
  );
};

/**
 * MSR3 Clients Summary page (`/workspace/:workspaceId/clients/summary/:clientId`).
 * Header (client name + Representation / Representation Start Date, matching
 * `TitleSummaryV3`'s header shape) + Tasks (S4) + Client Titles (S2) +
 * Statements (S3, a tab per service the client is registered for or has
 * listed records under — see `ClientStatementsSection`).
 *
 * Shape mirrors `TitleSummaryV3`: a single root query gates the whole page
 * through one `SingleQueryResults`, so loading and error states render
 * `MSR3BaseLayout` themselves and the sidebar/mobile chrome stays mounted in
 * every state, not just success.
 */
const ClientSummaryV3 = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const [activeWorkspace] = useActiveWorkspace();

  const clientQuery = useQuery(queryOptionsGetClient(clientId));

  if (!clientId) return null;

  return (
    <SingleQueryResults
      errorFallback={(error) => (
        <MSR3BaseLayout
          classes={{ childrenWrapper: 'space-y-8' }}
          pageHeader={<PageHeader title="Unable to load client" />}
        >
          <ErrorFallback error={error} />
        </MSR3BaseLayout>
      )}
      loadingFallback={
        <MSR3BaseLayout
          classes={{ childrenWrapper: 'space-y-8' }}
          pageHeader={<Skeleton className="h-10 w-72" />}
        >
          <Skeleton className="h-64 w-full" />
        </MSR3BaseLayout>
      }
      query={clientQuery}
    >
      {(client) => {
        // The header above comes from `client`, fetched globally by
        // clientId alone, while every section below (Tasks / Titles /
        // Statements) scopes its own query to the active workspace. Without
        // this guard, opening another workspace's client by id would render
        // a populated header over sections that all come back empty. Only
        // compare once `activeWorkspace` has actually resolved, so we don't
        // flash a not-found state during the loading window.
        const isWrongWorkspace =
          activeWorkspace != null && client.workspaceId !== activeWorkspace._id;

        // Rendered bare, without MSR3BaseLayout: the clients list does the
        // same for its own denial (APP-2375, `ClientsV3`), so a client that
        // isn't in this workspace looks identical whichever way you reach it.
        if (isWrongWorkspace) return <ErrorPage notFound />;

        return (
          <MSR3BaseLayout
            classes={{ childrenWrapper: 'space-y-8' }}
            pageHeader={<ClientSummaryHeader client={client} />}
          >
            <ClientTasksSection clientId={client._id} />
            <ClientTitlesSection clientId={client._id} />
            <ClientStatementsSection clientId={client._id} />
          </MSR3BaseLayout>
        );
      }}
    </SingleQueryResults>
  );
};

export { ClientSummaryV3 };
